import * as Sentry from '@sentry/angular';
import { BidAdaptersComponent } from './../components/publication/bid-adapters/bid-adapters.component';
import { BbToastrComponent } from './../components/shared/bb-toastr/bb-toastr.component';
import { BbPreviewPageComponent } from './../components/shared/bb-preview-page/bb-preview-page.component';
import { PlaylistDetailsComponent } from './../components/library/playlist/playlist-details.component';
import { NavigateAwayGuard } from './../components/shared/abstract-details.component';
import { MediaclipDetailsComponent } from './../components/library/mediaclip/mediaclip-details.component';
import { EventService } from '../services/event-service';
import { AssetService } from './../services/asset.service';
import { NgModule, Injector, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { Router } from '@angular/router';
import { AspectRatioService } from './../services/aspect-ratio.service';
import { ReduxService } from '../services/redux.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Routes, RouterModule } from '@angular/router';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { LocalStorageModule } from 'angular-2-local-storage';
import { HttpClientModule, HttpClientJsonpModule } from '@angular/common/http';

import { ClipboardModule } from 'ngx-clipboard';
import { HotkeyModule } from 'angular2-hotkeys';
import { OverlayModule } from './overlay.module';
import { AnalyticsModule } from './analytics.module';
import { InteractivityModule } from './interactivity.module';
import { LibraryModule } from './library.module';
import { AdservicesModule } from './adservices.module';
import { PublicationModule } from './publication.module';
import { SharedModule } from './shared.module';
import { LoginModule } from './login.module';

import { QuickeditService } from './../services/quickedit.service';

import { AppComponent } from '../components/app.component';

import { AuthGuard } from '../guards/auth.guard';

import { BbTableModule } from './bb-table.module';
import { PipesModule } from './pipes.module';

import { WebSocketsService } from '../services/web-sockets.service';
import { TranscodingService } from '../services/transcoding.service';
import { ModalRouteService } from './../services/modal-route.service';
import { NgReduxModule } from '@angular-redux/store';

import { AdBlockDetectorService } from '../services/ad-block-detector.service';
import { DatePresetsSelectorService } from '../services/date-presets-selector.service';
import { PlayoutService } from './../services/playout.service';

import { SapiModule } from './sapi.module';
import { DevScreenComponent } from '../components/dev/dev-screen.component';
import { DashboardComponent } from '../components/dashboard/dashboard.component';
import { DashboardMediaComponent } from '../components/dashboard/dashboard-media.component';
import { DashboardAdsComponent } from '../components/dashboard/dashboard-ads.component';
import { BbNavigationComponent } from '../components/shared/bb-navigation/bb-navigation.component';
import { ContainerComponent } from '../components/user/container.component';
import { StatisticsService } from '../services/statistics.service';
import { Observable, of } from 'rxjs';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { AbstractModule } from './abstract.module';
import { MatIconModule } from '@angular/material/icon';
import { DateAdapter } from '@angular/material/core';
import { LocationStrategy } from '../../node_modules/@angular/common';
import { ParameterHashLocationStrategy } from '../classes/parameter-hash-location-strategy';
import { ConfigurableDateAdapter } from '../classes/configurable-date-adapter';
import { ToastrModule } from 'ngx-toastr';
import { SubtitleQuickEditComponent } from './../components/library/mediaclip/subtitle-quickedit.component';

export class TranslationLoader implements TranslateLoader {
	getTranslation(lang: string): Observable<any> {
		const translations = require('../resources/i18n/en_US.json');
		const help = require('../resources/help/en_US.json');

		return of({ ...translations, ...help });
	}
}

// https://stackoverflow.com/a/40592524/4746171
export class ServiceLocator {
	static injector: Injector;
}

const appRoutes: Routes = [
	{
		path: '',
		canActivate: [AuthGuard],
		component: AppComponent,
		pathMatch: 'full'
	},
	{
		path: 'dev',
		canActivate: [AuthGuard],
		component: ContainerComponent,
		pathMatch: 'full',
		data: {component: 'dev-screen', class: 'has-header has-buttonbar', permissions: ['bluebillywig-dev']}
	},
	{
		path: 'home',
		canActivate: [AuthGuard],
		component: ContainerComponent,
		pathMatch: 'full',
		data: {component: 'dashboard', class: 'has-header has-buttonbar'}
	},
	{
		path: 'preferences',
		component: ContainerComponent,
		data: {component: 'user-component', class: 'has-header has-buttonbar'}
	},
	{
		path: 'preview',
		canActivate: [AuthGuard],
		component: BbPreviewPageComponent,
		data: {
			component: 'bb-preview-page', class: ''
		}
	},
	{
		path: '**',
		redirectTo: 'home'
	},
	// modal paths
	{ path: 'library/mediaclip/:id/:tab', outlet: 'modal', component: MediaclipDetailsComponent, canDeactivate: [NavigateAwayGuard] },
	{ path: 'publication/global-prebid-settings/:tab', outlet: 'modal', component: BidAdaptersComponent, canDeactivate: [NavigateAwayGuard] },
	{ path: 'library/playlist/:id/:tab', outlet: 'modal', component: PlaylistDetailsComponent, canDeactivate: [NavigateAwayGuard] },
	{ path: 'mediaclip/:parentid/subtitle/:id', outlet: 'modal', component: SubtitleQuickEditComponent, canDeactivate: [NavigateAwayGuard] },
];

@NgModule({
	imports: [
		HttpClientModule,
		HttpClientJsonpModule,
		SharedModule,
		BrowserModule,
		HammerModule,
		AnalyticsModule,
		InteractivityModule,
		LibraryModule,
		AdservicesModule,
		PublicationModule,
		OverlayModule,
		RouterModule.forRoot(appRoutes, {
			// enableTracing: true, // for debugging the router
			useHash: true,
			relativeLinkResolution: 'legacy'
		}),
		PipesModule,
		HotkeyModule.forRoot(),
		LocalStorageModule.forRoot({
			prefix: '',
			storageType: 'localStorage'
		}),
		ClipboardModule,
		BrowserAnimationsModule,
		NgReduxModule,
		SapiModule,
		BbTableModule,
		LoginModule,
		ToastrModule.forRoot({
			toastComponent: BbToastrComponent,
			positionClass: 'toast-top-center'
		}),
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader, useClass: TranslationLoader
			}
		}),
		AbstractModule,
		MatIconModule
	],
	exports: [
		RouterModule,
		ToastrModule,
		BbNavigationComponent
	],
	declarations: [
		AppComponent,
		BbNavigationComponent,
		DevScreenComponent,
		DashboardComponent,
		DashboardAdsComponent,
		DashboardMediaComponent,
		ContainerComponent
	],
	providers: [
		{
			provide: 'AuthenticationService',
			useFactory: (i: any) => i.get('authenticationService'),
			deps: ['$injector']
		},
		{
			provide: LocationStrategy,
			useClass: ParameterHashLocationStrategy
		},
		{
			provide: DateAdapter,
			useClass: ConfigurableDateAdapter
		},
		// Sentry
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler({
			  showDialog: false,
			}),
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		{
			provide: APP_INITIALIZER,
			useFactory: () => () => {},
			deps: [Sentry.TraceService],
			multi: true,
		},
		AspectRatioService,
		AssetService,
		QuickeditService,
		WebSocketsService,
		AdBlockDetectorService,
		ReduxService,
		TranscodingService,
		DevScreenComponent,
		DashboardComponent,
		BbNavigationComponent,
		DatePresetsSelectorService,
		EventService,
		PlayoutService,
		StatisticsService,
		ModalRouteService
	],
	bootstrap: [AppComponent]
})

export class AppModule {
	constructor(private injector: Injector, private dateAdapter: DateAdapter<Date>) {
		ServiceLocator.injector = this.injector;
		this.dateAdapter.setLocale('en-GB');
	}
}
